import React, { useEffect, useState } from 'react';
import { useConfig, useAppState } from '@ynap/immutables-provider';
import { Person, ResponseError } from '@ynap/api-utils';
import { raygun } from '@ynap/clientside-monitoring';
import 'cross-fetch/polyfill';

const configSelector = (config) => ({
    baseUrl: config.global.services.commerce.baseUrl,
    brandId: config.global.application.brandId,
    clientId: config.global.services.commerce.clientId,
    clientSecret: config.global.services.commerce.clientSecret,
});

// TODO: Handle these better i.e. update ubertoken / auto retry in some cases
// wcs-fetch that can handle these is coupled with redux
const AUTH_ERRORS = [
    '_ERR_EXPIRED_ACTIVITY_TOKEN',
    '_ERR_TERMINATED_ACTIVITY_TOKEN',
    'CWXBB1012E',
    '_ERR_INVALID_COOKIE',
    '_ERR_INVALID_ACTIVITY_TOKEN',
    'NOT_AUTHORIZED_FOR_QUERY',
    '_ERR_ACTIVITY_TOKEN',
    '_ERR_UBF_USER_AUTHORITY',
    'ERR_PARTIAL_AUTHENTICATION_NOT_ALLOWED',
    'ERR_AUTHENTICATION_REQUIRED',
    'ERR_OLD_ENCRYPTION_KEY',
    'ERR_SESSION_AFFINITY',
];

const useUserPreferences = (uberToken, preferenceId) => {
    const [preferences, setPreferences] = useState();
    const [preferencesLoading, setPreferencesLoading] = useState(true);
    const [loading, setLoading] = useState();
    const [errors, setErrors] = useState({ addUserPreference: null, removeUserPreference: null, getUserPreferences: null });
    const [authenticated, setAuthenticated] = useState(true);

    const { baseUrl, brandId, clientId, clientSecret } = useConfig(configSelector);
    const { country } = useAppState();

    const setApiErrors = (api, json) => {
        const error = json?.errors?.[0]?.errorKey;
        setAuthenticated(!AUTH_ERRORS.includes(error));
        setErrors({ ...errors, [`${api}`]: error || 'error' });
    };

    const clearApiErrors = (api) => {
        setAuthenticated(true);
        setErrors({ ...errors, [`${api}`]: null });
    };

    const addUserPreference = async (body, preference = preferenceId) => {
        try {
            if (loading) return;
            setLoading(true);
            const params = { baseUrl, brandId, country, preference, body, uberToken, clientId };
            const request = Person.userPreferenceUpdate(params);

            const response = await fetch(request);

            response && setLoading(false);
            if (response.ok && preference) {
                const { values } = body || {};
                setPreferences([...(preferences || []), ...values]);
                clearApiErrors('addUserPreference');
            } else if (!response.ok) {
                const json = await response?.json();
                setApiErrors('addUserPreference', json);
                throw new ResponseError(response);
            }

            return response.json();
        } catch (error) {
            setLoading(false);
            raygun.helpers.agent('send', {
                error: new Error(`addUserPreference error`),
                tags: [`error-addUserPreference`, `preferenceId: ${preference}`],
                customData: { error, body },
            });
        }
    };

    const removeUserPreference = async (body, preference = preferenceId) => {
        try {
            if (loading) return;
            setLoading(true);
            const params = { baseUrl, brandId, country, preference, body, uberToken, clientId };
            const request = Person.userPreferenceRemove(params);

            const response = await fetch(request);

            response && setLoading(false);
            if (response.ok && preference) {
                setPreferences((preferences || []).filter(({ name }) => !body.includes(name)));
                clearApiErrors('removeUserPreference');
            } else if (!response.ok) {
                const json = await response?.json();
                setApiErrors('removeUserPreference', json);
                throw new ResponseError(response);
            }

            return response.json();
        } catch (error) {
            setLoading(false);
            raygun.helpers.agent('send', {
                error: new Error(`removeUserPreference error`),
                tags: [`error-removeUserPreference`, `preferenceId: ${preference}`],
                customData: { error, body },
            });
        }
    };

    const filterPreferences = (preferenceList, preference = preferenceId) => {
        const PREFERENCE = String(preference || '').toUpperCase();
        return Array.isArray(preferenceList) ? preferenceList.find((pref) => String(pref.name).toUpperCase() === PREFERENCE)?.values || [] : [];
    };

    const getUserPreferences = async (preference = preferenceId) => {
        try {
            const params = { baseUrl, brandId, country, uberToken, clientId, clientSecret };
            const request = Person.getUserPreferences(params);
            const response = await fetch(request);
            const json = await response?.json();

            if (response.ok) {
                setPreferences(preference ? filterPreferences(json?.userPreferences) : json?.userPreferences);
                setPreferencesLoading(false);
                clearApiErrors('getUserPreferences');
            } else if (!response.ok) {
                setPreferences(null);
                setPreferencesLoading(false);
                setApiErrors('getUserPreferences', json);
                throw new ResponseError(response);
            }
        } catch (error) {
            setPreferencesLoading(false);
        }
    };

    useEffect(() => {
        getUserPreferences().catch(() => {
            setPreferencesLoading(false);
            if (!uberToken) setPreferences(null);
        });
    }, []);

    return {
        preferences,
        addUserPreference,
        removeUserPreference,
        getUserPreferences,
        filterPreferences,
        loadingFavourite: loading,
        preferencesLoading,
        errors,
        authenticated,
    };
};

export const withUserPreferences = (Component) => (props) => {
    const { uberToken, preferenceId } = props;
    const userPreferences = useUserPreferences(uberToken, preferenceId);

    return <Component {...props} {...userPreferences} />;
};

export default useUserPreferences;
